import { useMemo } from "react";

import { useSelector } from "react-redux";

import { selectIsSalesforceCompanyAsLeadExportEnabled } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforceSelectors";

import type { ExportCostTypeItem } from "../enums";
import { useDataCreditsQuery } from "../queries/useDataCreditsQuery";
import { useStaticLeadsDataCreditsCostsQuery } from "../queries/useStaticLeadsDataCreditsCostsQuery";
import type { CostTypeMapper, ExportCostState } from "../types";
import { calculateExportPricePerRecord } from "../utils/dataCredits";

import { useExportQuota } from "./useExportQuota";

type HookParams<T extends string> =
    | { columnTypes: readonly T[]; costTypeMapper: CostTypeMapper<T> }
    | {
          exportType: ExportCostTypeItem;
      };

export const useLeadsExportCost = <T extends string>(params: HookParams<T>): ExportCostState => {
    const isCompanyAsLeadExportEnabled = useSelector(selectIsSalesforceCompanyAsLeadExportEnabled);

    const { data: dataPoints, isLoading: isDataCreditsLoading } = useDataCreditsQuery({
        select: (data) => data.dataPoints,
    });

    const { data: exportCredits, isFetching: isFlatCostFetching } = useStaticLeadsDataCreditsCostsQuery();

    const { isLoading: isQuotaLoading, ...quota } = useExportQuota();

    return useMemo(() => {
        const remaining = quota.data.remaining;

        const pricePerRecord = calculateExportPricePerRecord(
            "exportType" in params
                ? {
                      ...params,
                      costPerRecord: exportCredits?.costPerRecord,
                      isCompanyAsLeadExport: isCompanyAsLeadExportEnabled,
                  }
                : { ...params, dataPoints },
        );

        return {
            isLoading: isDataCreditsLoading || isFlatCostFetching || isQuotaLoading,
            quota,
            pricePerRecord,
            canAfford: (cost: number) => quota.data.remaining >= cost,
            getExportCost: (count: number) => count * pricePerRecord,
            isLowQuota: (count: number) => remaining < count * pricePerRecord || remaining === 0,
        };
    }, [isDataCreditsLoading, isFlatCostFetching, isQuotaLoading, quota.data]);
};
